import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPut, operationsClient } from '@/lib/api/client';
import { taxCodeEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';
import type { AppliesToOverrideValue } from './useSetAppliesToOverride';

/**
 * Full-update hook for an existing tax_code (PUT /tax-codes/{id}).
 *
 * Used by `EditTaxCodeDialog` when the user opens the dialog on a row
 * that already has a matching tax_code at the requested jurisdiction
 * level (i.e. edit mode, not create mode). The "Save" path commits
 * `name` + `rate` (and any other surfaced fields) in a single roundtrip.
 *
 * The raw `appliesTo` field is intentionally **not** written through this
 * hook (writing it directly bypasses the override pipeline, and the next
 * Ziptax populate would clobber it). Instead pass `appliesToOverride` —
 * the PUT endpoint routes it through the same override pipeline as
 * `PATCH /tax-codes/:id/applies-to-override` (sets the override + recomputes
 * the effective `appliesTo`, preserving the Ziptax original). This lets the
 * edit dialog commit rate + applies-to in a single request.
 */
export interface UpdateTaxCodePayload {
  id: string;
  name?: string;
  rate?: number;
  rateOverride?: number | null;
  rateType?: 'percentage' | 'fixed';
  /** Applies-to override. `null`/`[]` clears it (revert to Ziptax-derived). */
  appliesToOverride?: AppliesToOverrideValue;
}

export function useUpdateTaxCode() {
  const qc = useQueryClient();
  return useApiMutation<void, UpdateTaxCodePayload>({
    mutationFn: ({ id, ...rest }) =>
      apiPut<void>(operationsClient, taxCodeEndpoints.update(id), rest),
    onSuccess: () => {
      toast.success('Tax rate updated.');
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    },
  });
}
